import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateConversation } from "../../hooks/useCreateConversation";
import { useMyAssistant } from "../../hooks/useMyAssistant";
import { generateDefaultTitle } from "../../utils/conversation";
import { toast } from "sonner";
import type { ConversationData } from "../../types/conversation";

interface CreateConversationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (conversation: ConversationData) => void;
}

export function CreateConversationModal({ 
  open, 
  onClose, 
  onSuccess 
}: CreateConversationModalProps) {
  const [title, setTitle] = useState("");
  const { createConversation, loading } = useCreateConversation();
  const { assistant } = useMyAssistant();
  
  const handleCreate = async () => {
    if (!assistant?.id) {
      toast.error("No assistant found. Please set up your assistant first.");
      return;
    }

    try {
      const conversation = await createConversation({
        title: title.trim() || generateDefaultTitle(),
        assistantId: assistant.id,
      });
      
      if (conversation) {
        onSuccess?.(conversation as ConversationData);
        onClose();
        toast.success("Conversation created");
        setTitle(""); // Reset form
      }
    } catch (error) {
      console.error("Failed to create conversation:", error);
      toast.error("Failed to create conversation");
    }
  };
  
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTitle(""); // Reset form when closing
      onClose();
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start New Conversation</DialogTitle>
          <DialogDescription>
            Give your conversation a title to help you remember what it's about.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title (optional)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Project planning, Tax questions..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && !loading) {
                  handleCreate();
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading || !assistant}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Conversation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}